import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import {
  Modal,
  Button,
  FormLayout,
  Select,
  TextField as PTextField,
  Banner,
  Spinner,
  Stack,
  TextStyle,
} from "@shopify/polaris"
import { CirclePlusMinor } from "@shopify/polaris-icons"
import { useTranslation } from "react-i18next"
import { useEntity } from "hooks/useAxios"
import AiServicesEntity from "services/entities/AiServicesEntity"
import { locations } from "helpers/CollectHelper"
import useFieldIsLocked from "components/widgets/CollectPage/forms/fields/useFieldIsLocked"
import { useCollectContext } from "components/widgets/CollectPage/CollectContextProvider"

// Constants
const MAX_INTEGER_VALUE = 1000000000

/**
 * Resolve parameter value
 * - integer → returned as is in string
 * - object { min, max } → average of min and max in string
 * - null/undefined → empty string
 */
const resolveParameter = (param) => {
  if (param === null || param === undefined) return ""
  if (typeof param === "number") return String(param)
  if (
    typeof param === "object" &&
    param.min !== undefined &&
    param.max !== undefined
  ) {
    return String((param.min + param.max) / 2)
  }
  return ""
}

// Utility functions
const formatNumericValue = (value) => {
  return value == null ? "" : String(value)
}

const shouldLoadProviders = (open) => {
  return open
}

const getErrorMessage = (error, defaultMessage) => {
  return error?.response?.data?.error || error?.message || defaultMessage
}

const createFormDataFromService = (aiService) => {
  if (!aiService) {
    return {
      project_name: "",
      use_case_name: "",
      use_case_type: "",
      use_case_goal: "",
      personae: "",
      provider: "",
      model: "",
      active_parameters_b: "",
      total_parameters_b: "",
      output_completion_tokens: "",
      weekly_users: "",
      weekly_usage: "",
      country_location: "",
      example_prompts: "",
      status: "active",
      source: "manual",
    }
  }

  return {
    project_name: aiService.project_name || "",
    use_case_name: aiService.use_case_name || "",
    use_case_type: aiService.use_case_type || "",
    use_case_goal: aiService.use_case_goal || "",
    personae: aiService.personae || "",
    provider: aiService.provider || "",
    model: aiService.model || "",
    active_parameters_b: formatNumericValue(aiService.active_parameters_b),
    total_parameters_b: formatNumericValue(aiService.total_parameters_b),
    output_completion_tokens: formatNumericValue(
      aiService.output_completion_tokens
    ),
    weekly_users: formatNumericValue(aiService.weekly_users),
    weekly_usage: formatNumericValue(aiService.weekly_usage),
    country_location: aiService.country_location || "",
    example_prompts: aiService.example_prompts || "",
    status: aiService.status || "active",
    source: aiService.source || "manual",
  }
}

const transformNumericFields = (formData) => {
  const numericFields = [
    "active_parameters_b",
    "total_parameters_b",
    "output_completion_tokens",
    "weekly_users",
    "weekly_usage",
  ]

  const transformed = { ...formData }
  numericFields.forEach((field) => {
    transformed[field] = formData[field]
      ? Number.parseFloat(formData[field])
      : null
  })

  return transformed
}

const ModalAiService = ({
  aiService = null,
  postAction,
  iconOnly = false,
  children,
}) => {
  const { t } = useTranslation()
  const { isLocked } = useFieldIsLocked()
  const { collect } = useCollectContext()
  const aiServicesEntity = useEntity(AiServicesEntity)

  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState({})
  const [isSending, setIsSending] = useState(false)
  const [errors, setErrors] = useState({})
  const [apiError, setApiError] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  const [providerOptions, setProviderOptions] = useState([])
  const [modelOptions, setModelOptions] = useState([])
  const [providersWithModels, setProvidersWithModels] = useState({})
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)
  const [providersLoadError, setProvidersLoadError] = useState(null)
  const providersLoadedRef = useRef(false)
  const [options, setOptions] = useState({
    useCaseType: [],
    useCaseGoal: [],
    examplePrompts: [],
    personae: [],
  })

  const countries = locations({
    additionalKeys: [{ label: "", value: "" }],
  })

  // Helper function to create option items
  const createOptions = (keys, translationPath, withBlank = false) => {
    const items = keys.map((key) => ({
      label: t(`${translationPath}.${key}`),
      value: key,
    }))
    return withBlank ? [{ label: "", value: "" }, ...items] : items
  }

  useEffect(() => {
    setOptions({
      useCaseType: createOptions(
        [
          "text_generation",
          "text_summarization",
          "text_classification",
          "code_generation",
          "other",
        ],
        "collect:questions.13.ai_services_options.use_case_type",
        true
      ),
      useCaseGoal: createOptions(
        ["productivity", "competitive_advantage", "other"],
        "collect:questions.13.ai_services_options.use_case_goal",
        true
      ),
      examplePrompts: createOptions(
        [
          "email_drafting",
          "code_generation",
          "translation",
          "summarization",
          "question_answering",
          "other",
        ],
        "collect:questions.13.ai_services_options.example_prompts",
        true
      ),
      personae: createOptions(
        [
          "business",
          "employees",
          "support_functions",
          "it",
          "sales",
          "customer_support",
          "other",
        ],
        "collect:questions.13.ai_services_options.personae",
        true
      ),
    })
  }, [t])

  useEffect(() => {
    if (!open) {
      providersLoadedRef.current = false
      setIsLoadingProviders(false)
      return
    }

    if (providersLoadedRef.current) {
      setIsLoadingProviders(false)
      return
    }

    let cancelled = false

    const loadProviders = async () => {
      setIsLoadingProviders(true)
      setProvidersLoadError(null)
      try {
        const response = await aiServicesEntity.getProvidersWithModels()

        if (cancelled) {
          setIsLoadingProviders(false)
          return
        }

        if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          setProvidersWithModels(response)
          setProviderOptions(
            Object.keys(response).map((key) => ({
              label: response[key].display_name || key,
              value: key,
            }))
          )
          providersLoadedRef.current = true

          // Load models if a provider is already selected
          const currentProvider = aiService?.provider || formData.provider
          if (currentProvider && response[currentProvider]?.models) {
            setModelOptions(
              response[currentProvider].models.map((model) => ({
                label: model.display_name || model.name,
                value: model.name,
              }))
            )
          }
        } else {
          setProvidersLoadError(
            t("collect:questions.13.ai_services.errors.providers_load_failed")
          )
          providersLoadedRef.current = true
        }
      } catch (error) {
        if (!cancelled) {
          const errorMessage = getErrorMessage(
            error,
            t("collect:questions.13.ai_services.errors.providers_load_failed")
          )
          setProvidersLoadError(errorMessage)
          setProvidersWithModels({})
          setProviderOptions([])
          providersLoadedRef.current = true
        }
      } finally {
        if (!cancelled) {
          setIsLoadingProviders(false)
        }
      }
    }

    loadProviders()

    return () => {
      cancelled = true
      setIsLoadingProviders(false)
    }
  }, [open])

  useEffect(() => {
    setFormData(createFormDataFromService(aiService))
    setErrors({})
    setApiError(null)
    setDeleteConfirm(false)

    if (!aiService) {
      setModelOptions([])
    }

    // Load models if providers are already loaded and a provider is selected
    if (
      providersLoadedRef.current &&
      providersWithModels &&
      Object.keys(providersWithModels).length > 0
    ) {
      const provider = aiService?.provider || formData.provider
      if (provider && providersWithModels[provider]?.models) {
        setModelOptions(
          providersWithModels[provider].models.map((model) => ({
            label: model.display_name || model.name,
            value: model.name,
          }))
        )
      }
    }
  }, [aiService, open, providersWithModels])

  useEffect(() => {
    // Only load models if providers are loaded
    if (
      !providersLoadedRef.current ||
      !providersWithModels ||
      Object.keys(providersWithModels).length === 0
    ) {
      return
    }

    const provider = aiService?.provider || formData.provider
    if (provider && providersWithModels[provider]?.models) {
      const models = providersWithModels[provider].models.map((model) => ({
        label: model.display_name || model.name,
        value: model.name,
      }))
      setModelOptions(models)
    } else if (!provider) {
      setModelOptions([])
    }
  }, [aiService?.provider, formData.provider, providersWithModels, open])

  const getWarningForNumericField = (value) => {
    if (!value || value === "") return null

    const numValue = Number.parseFloat(value)
    if (!Number.isNaN(numValue) && numValue > MAX_INTEGER_VALUE) {
      return t(
        "collect:questions.13.ai_services.errors.value_too_large_warning"
      )
    }
    return null
  }

  const handleNumericFieldChange = (fieldName, value, isFloat = true) => {
    // Filter unauthorized characters (for copy-paste protection)
    const filteredValue = value
      .replaceAll(/[eE]/g, "")
      .replaceAll("+", "")
      .replaceAll("-", "")

    const numValue = isFloat
      ? Number.parseFloat(filteredValue)
      : Number.parseInt(filteredValue, 10)

    // Accept if empty or if it's a valid positive number
    if (filteredValue === "" || (!Number.isNaN(numValue) && numValue >= 0)) {
      setFormData({ ...formData, [fieldName]: filteredValue })
      setErrors((prev) => ({
        ...prev,
        [fieldName]: undefined,
      }))
    }
  }

  const handleProviderChange = (providerId) => {
    setFormData((prev) => ({
      ...prev,
      provider: providerId,
      model: "",
      active_parameters_b: "",
      total_parameters_b: "",
    }))
    setErrors((prev) => ({ ...prev, provider: undefined, model: undefined }))

    if (providerId && providersWithModels[providerId]?.models) {
      setModelOptions(
        providersWithModels[providerId].models.map((model) => ({
          label: model.display_name || model.name,
          value: model.name,
        }))
      )
    } else {
      setModelOptions([])
    }
  }

  const handleModelChange = (modelName) => {
    const provider = formData.provider
    const modelData =
      provider &&
      providersWithModels[provider]?.models?.find((m) => m.name === modelName)

    setFormData((prev) => ({
      ...prev,
      model: modelName,
      ...(modelData
        ? {
            active_parameters_b: resolveParameter(modelData.active_parameters),
            total_parameters_b: resolveParameter(modelData.total_parameters),
          }
        : {}),
    }))
    setErrors((prev) => ({ ...prev, model: undefined }))
  }

  const handleSubmit = async () => {
    setIsSending(true)
    setApiError(null)
    setErrors({})

    try {
      // Do not send status or source, they are handled server-side
      const { status, source, ...payloadFormData } = formData

      const payload = {
        ai_service: transformNumericFields(payloadFormData),
      }

      if (aiService?.id) {
        await aiServicesEntity.decentralizedAiServiceUploadUpdate(
          aiService.id,
          payload
        )
      } else {
        await aiServicesEntity.decentralizedAiServiceUpload(payload)
      }

      setOpen(false)
      setDeleteConfirm(false)
      postAction?.()
    } catch (error) {
      const responseData = error?.response?.data
      if (
        responseData &&
        typeof responseData === "object" &&
        !Array.isArray(responseData) &&
        !responseData.error
      ) {
        const fieldErrors = {}
        Object.entries(responseData).forEach(([field, messages]) => {
          fieldErrors[field] = Array.isArray(messages) ? messages[0] : messages
        })
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors)
        } else {
          setApiError(t("collect:questions.13.ai_services.errors.save_failed"))
        }
      } else {
        setApiError(
          getErrorMessage(
            error,
            t("collect:questions.13.ai_services.errors.save_failed")
          )
        )
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleDelete = async () => {
    if (!aiService?.id) return

    setIsSending(true)
    setApiError(null)

    try {
      await aiServicesEntity.decentralizedAiServiceUploadDelete(aiService.id)
      setOpen(false)
      setDeleteConfirm(false)
      postAction?.()
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("collect:questions.13.ai_services.errors.delete_failed")
      )
      setApiError(message)
    } finally {
      setIsSending(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setDeleteConfirm(false)
    setIsLoadingProviders(false)
    setProvidersLoadError(null)
    providersLoadedRef.current = false
  }

  const getModalTitle = () => {
    if (aiService) {
      return t("collect:questions.13.ai_services.modal.edit")
    }
    return t("collect:questions.13.ai_services.modal.add")
  }

  const getSecondaryActions = () => {
    const actions = [
      {
        content: t("common:cancel"),
        onAction: handleClose,
      },
    ]

    if (aiService?.id && !deleteConfirm && !isLocked) {
      actions.push({
        content: t("common:delete"),
        onAction: () => setDeleteConfirm(true),
        destructive: true,
      })
    }

    return actions
  }

  const activator = children ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      style={{
        cursor: "pointer",
        background: "none",
        border: "none",
        padding: 0,
        font: "inherit",
        color: "inherit",
        textAlign: "inherit",
      }}
    >
      {children}
    </button>
  ) : (
    <Button
      onClick={() => setOpen(true)}
      icon={!aiService && iconOnly ? CirclePlusMinor : undefined}
      accessibilityLabel={aiService ? t("common:edit") : t("common:add")}
      plain={!aiService && iconOnly}
    >
      {(() => {
        if (aiService || !iconOnly) {
          return aiService ? t("common:edit") : t("common:add")
        }
        return null
      })()}
    </Button>
  )

  return (
    <>
      {activator}

      <Modal
        open={open}
        onClose={handleClose}
        title={getModalTitle()}
        primaryAction={
          deleteConfirm || collect?.closed_at !== null
            ? null
            : {
                content: aiService ? t("common:update") : t("common:save"),
                onAction: handleSubmit,
                loading: isSending,
                disabled: isLoadingProviders || isLocked,
              }
        }
        secondaryActions={getSecondaryActions()}
        large
      >
        <Modal.Section>
          {apiError && (
            <Banner status="critical" onDismiss={() => setApiError(null)}>
              {apiError}
            </Banner>
          )}

          {providersLoadError && (
            <Banner
              status="warning"
              onDismiss={() => setProvidersLoadError(null)}
            >
              {providersLoadError}
            </Banner>
          )}

          {deleteConfirm ? (
            <Banner
              status="warning"
              title={t("collect:questions.13.ai_services.modal.delete_title")}
            >
              <p>
                {t("collect:questions.13.ai_services.modal.delete_description")}
              </p>
              <Button destructive onClick={handleDelete} loading={isSending}>
                {t("common:delete")}
              </Button>
            </Banner>
          ) : (
            <>
              {isLoadingProviders && !providersLoadedRef.current && (
                <Stack alignment="center" spacing="tight">
                  <Spinner size="small" />
                  <p>
                    {t("collect:questions.13.ai_services.loading.providers")}
                  </p>
                </Stack>
              )}

              <FormLayout>
                <FormLayout.Group condensed>
                  <PTextField
                    label={t(
                      "collect:questions.13.ai_services.form.project_name"
                    )}
                    value={formData.project_name || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, project_name: value })
                    }
                    autoComplete="off"
                    error={errors.project_name}
                    disabled={isLocked}
                  />
                  <PTextField
                    label={t(
                      "collect:questions.13.ai_services.form.use_case_name"
                    )}
                    value={formData.use_case_name || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, use_case_name: value })
                    }
                    autoComplete="off"
                    error={errors.use_case_name}
                    disabled={isLocked}
                  />
                </FormLayout.Group>

                <FormLayout.Group condensed>
                  <Select
                    label={t(
                      "collect:questions.13.ai_services.form.use_case_type"
                    )}
                    options={options.useCaseType}
                    value={formData.use_case_type || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, use_case_type: value })
                    }
                    error={errors.use_case_type}
                    disabled={isLocked}
                  />
                  <Select
                    label={t(
                      "collect:questions.13.ai_services.form.use_case_goal"
                    )}
                    options={options.useCaseGoal}
                    value={formData.use_case_goal || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, use_case_goal: value })
                    }
                    error={errors.use_case_goal}
                    disabled={isLocked}
                  />
                </FormLayout.Group>

                <FormLayout.Group condensed>
                  <Select
                    label={t("collect:questions.13.ai_services.form.personae")}
                    options={options.personae}
                    value={formData.personae || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, personae: value })
                    }
                    error={errors.personae}
                    disabled={isLocked}
                  />
                  <Select
                    label={t("collect:questions.13.ai_services.form.provider")}
                    options={providerOptions}
                    value={formData.provider || ""}
                    onChange={handleProviderChange}
                    disabled={isLoadingProviders || isLocked}
                    error={errors.provider}
                    placeholder={t(
                      "collect:questions.13.ai_services.form.provider_placeholder"
                    )}
                  />
                </FormLayout.Group>

                <FormLayout.Group condensed>
                  <Select
                    label={t("collect:questions.13.ai_services.form.model")}
                    options={modelOptions}
                    value={formData.model || ""}
                    onChange={handleModelChange}
                    disabled={
                      !formData.provider || isLoadingProviders || isLocked
                    }
                    error={errors.model}
                    placeholder={t(
                      "collect:questions.13.ai_services.form.model_placeholder"
                    )}
                  />
                  <PTextField
                    label={t(
                      "collect:questions.13.ai_services.form.active_parameters_b"
                    )}
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.active_parameters_b || ""}
                    onChange={(value) =>
                      handleNumericFieldChange("active_parameters_b", value)
                    }
                    autoComplete="off"
                    error={errors.active_parameters_b}
                    helpText={getWarningForNumericField(
                      formData.active_parameters_b
                    )}
                    disabled={isLocked}
                  />
                </FormLayout.Group>

                <FormLayout.Group condensed>
                  <PTextField
                    label={t(
                      "collect:questions.13.ai_services.form.total_parameters_b"
                    )}
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.total_parameters_b || ""}
                    onChange={(value) =>
                      handleNumericFieldChange("total_parameters_b", value)
                    }
                    autoComplete="off"
                    error={errors.total_parameters_b}
                    helpText={getWarningForNumericField(
                      formData.total_parameters_b
                    )}
                    disabled={isLocked}
                  />
                  <PTextField
                    label={t(
                      "collect:questions.13.ai_services.form.output_completion_tokens"
                    )}
                    type="number"
                    min="0"
                    value={formData.output_completion_tokens || ""}
                    onChange={(value) =>
                      handleNumericFieldChange(
                        "output_completion_tokens",
                        value
                      )
                    }
                    autoComplete="off"
                    error={errors.output_completion_tokens}
                    helpText={getWarningForNumericField(
                      formData.output_completion_tokens
                    )}
                    disabled={isLocked}
                  />
                </FormLayout.Group>

                <FormLayout.Group condensed>
                  <PTextField
                    label={t(
                      "collect:questions.13.ai_services.form.weekly_users"
                    )}
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.weekly_users || ""}
                    onChange={(value) =>
                      handleNumericFieldChange("weekly_users", value)
                    }
                    autoComplete="off"
                    error={errors.weekly_users}
                    helpText={getWarningForNumericField(formData.weekly_users)}
                    disabled={isLocked}
                  />
                  <PTextField
                    label={t(
                      "collect:questions.13.ai_services.form.weekly_usage"
                    )}
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.weekly_usage || ""}
                    onChange={(value) =>
                      handleNumericFieldChange("weekly_usage", value)
                    }
                    autoComplete="off"
                    error={errors.weekly_usage}
                    helpText={getWarningForNumericField(formData.weekly_usage)}
                    disabled={isLocked}
                  />
                </FormLayout.Group>

                <FormLayout.Group condensed>
                  <Select
                    label={t(
                      "collect:questions.13.ai_services.form.example_prompts"
                    )}
                    options={options.examplePrompts}
                    value={formData.example_prompts || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, example_prompts: value })
                    }
                    error={errors.example_prompts}
                    disabled={isLocked}
                  />
                  <Select
                    label={t(
                      "collect:questions.13.ai_services.form.country_location"
                    )}
                    options={countries}
                    value={formData.country_location || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, country_location: value })
                    }
                    error={errors.country_location}
                    disabled={isLocked}
                  />
                </FormLayout.Group>

                <input type="hidden" value={formData.source || "manual"} />
              </FormLayout>
              <br />
              <TextStyle variation="subdued">
                {t("collect:questions.common.nota_bene")}
              </TextStyle>
            </>
          )}
        </Modal.Section>
      </Modal>
    </>
  )
}

ModalAiService.propTypes = {
  aiService: PropTypes.object,
  postAction: PropTypes.func,
  iconOnly: PropTypes.bool,
  children: PropTypes.node,
}

export default ModalAiService
